import React, {ChangeEvent, useState} from 'react';
import Select from '@material-ui/core/Select';
import styled from 'styled-components';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import FormControl from '@material-ui/core/FormControl';
import {
  mdiTagOutline,
  mdiEarth,
  mdiOfficeBuildingOutline,
  mdiTrafficLight,
  mdiHelpRhombusOutline,
} from '@mdi/js';
import Icon from '@mdi/react';

const StyledSelect = styled(Select)`
  .MuiOutlinedInput-notchedOutline {
    border: 0;
  }

  .MuiSelect-select {
    border-radius: 20px;
    border: 1px solid ${props => props.theme.disabledStateBlue};
    top: 0px;
    transition: 100ms ease-in-out;
    transition-property: border;
  }
  &:hover .MuiSelect-select {
    border: 1px solid ${props => props.theme.disabledStateBlue};
    background-color: ${props => props.theme.disabledStateBlue}33;
  }
  &:active .MuiSelect-select,
  &.Mui-focused .MuiSelect-select {
    border: 1px solid ${props => props.theme.activeStateBlue};
    background-color: ${props => props.theme.activeStateBlue}80;
  }

  .MuiOutlinedInput-input {
    font-family: Roboto;
    padding: 7px 34px 7px 12px;
    font-size: 12px;
    display: flex;
    align-items: center;
    transition: 100ms ease-in-out;
    color: ${props => props.theme.disabledStateBlue};
  }
  &:hover .MuiOutlinedInput-input,
  &.Mui-focused .MuiOutlinedInput-input {
    color: ${props => props.theme.activeStateBlue};
  }

  svg {
    color: ${props => props.theme.disabledStateBlue};
    width: 16px;
    transition: 100ms ease-in-out;
    transition-property: color;
  }
  &:hover svg,
  &.Mui-focused svg {
    color: ${props => props.theme.activeStateBlue};
  }

  em {
    font-style: normal;
    margin-top: 2px;
  }

  legend {
    display: none;
  }

  &.has-value .MuiSelect-select {
    background-color: ${props => props.theme.activeStateBlue};
    color: ${props => props.theme.defaultNavy};
  }
  &.has-value svg {
    color: ${props => props.theme.defaultNavy};
  }
`;

const StyledMenuItem = styled(MenuItem)`
  min-width: 150px;
  font-family: Roboto;
  padding-left: 45px;
  transition: 100ms ease-in-out;
  transition-property: color, background-color;
  &.Mui-selected,
  &:hover {
    color: ${props => props.theme.defaultNavy};
    background-color: ${props => props.theme.activeStateBlue};
  }
  &.Mui-selected:hover {
    color: ${props => props.theme.defaultNavy};
    background-color: ${props => props.theme.activeStateBlue}B3;
  }
`;

const StyledIcon = styled(Icon)`
  margin-right: 10px;
`;

const StyledPaper = styled(Paper)`
  background-color: ${props => props.theme.cardBg};
  color: ${props => props.theme.text};
  border: 1px solid ${props => props.theme.lightGrey};
  border-radius: 0;
  margin-top: 45px;
  max-height: 55vh;
`;

const renderIcon = (labelKey: string) => {
  switch (labelKey) {
    case 'Brand':
      return <StyledIcon path={mdiTagOutline} title="Brand" size={1} />;
    case 'Geo':
      return <StyledIcon path={mdiEarth} title="Geo" size={1} />;
    case 'Site':
      return <StyledIcon path={mdiOfficeBuildingOutline} title="Site" size={1} />;
    case 'Status':
      return <StyledIcon path={mdiTrafficLight} title="Status" size={1} />;
    default:
      return <StyledIcon path={mdiHelpRhombusOutline} title="Unknown" size={1} />;
  }
};

interface FilterDropdownProps {
  label: string;
  filterKey: string;
  selectedValue: string;
  options: Record<string, {label: string}>;
  handleFilterChange: (filterKey: string, value: string) => void;
  filteredValueKeys: string[];
  isDisabled: boolean;
}

export default function FilterDropdown({
  label,
  filterKey,
  selectedValue,
  options,
  handleFilterChange,
  filteredValueKeys,
  isDisabled,
}: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const handleChange = (event: ChangeEvent<{value: unknown}>) => {
    const value = event.target.value as string;
    handleFilterChange(filterKey, value);
  };

  const handleClick = (value: string) => {
    if (selectedValue === value) {
      handleFilterChange(filterKey, value);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const renderValue = (value: any) => {
    if (!value) {
      return (
        <>
          {renderIcon(label)} {label}
        </>
      );
    } else {
      return (
        <>
          {renderIcon(label)} {options[value].label}
        </>
      );
    }
  };
  return (
    <FormControl variant="outlined">
      <StyledSelect
        disabled={isDisabled}
        labelId={label + 'select'}
        id={label + 'select'}
        value={selectedValue}
        onChange={handleChange}
        onClose={handleClose}
        open={open}
        label={label}
        renderValue={renderValue}
        onOpen={handleOpen}
        MenuProps={{
          getContentAnchorEl: null,
          PaperProps: {
            component: StyledPaper,
          },
          transformOrigin: {
            horizontal: 'left',
            vertical: 'top',
          },
        }}
        displayEmpty
        className={selectedValue !== '' ? 'has-value' : ''}
      >
        {filteredValueKeys.map(key => {
          return (
            <StyledMenuItem key={key} value={key} onClick={() => handleClick(key)}>
              {options[key].label}
            </StyledMenuItem>
          );
        })}
      </StyledSelect>
    </FormControl>
  );
}
